<script>
import CardList from '@/views/CardList'
import Button from '@/components/Button'
import ImageEnlarger from '@/components/ImageEnlarger'
import LostObjectsServices from '@/services/lostObjects'
import EnvironmentUtils from '@/utils/environmentUtils'
import GeolocationUtils from '@/utils/geolocationUtils'
import PDFUtils from '@/utils/PDFUtils'
import FileUtils from '@/utils/fileUtils'
import StringUtils from '@/utils/stringUtils'
import DateUtils from '@/utils/dateUtils'
import ArrayUtils from '@/utils/arrayUtils'

export default
{
    name: 'lost-objects-management',

    created ()
    {
        LostObjectsServices.getLostObjectsTagsDefinition(EnvironmentUtils.cityId, this.$store.state.user.token).then(async tags =>
        {
            await this.$store.commit('lostObjects/setTags', tags)

            const objects = await LostObjectsServices.fetchLostObjects(EnvironmentUtils.cityId, this.$store.state.user.token)
            await this.$store.commit('lostObjects/updateObjectsList', objects)

            if (this.$refs.cardList) this.$refs.cardList.selectMenuItemFromCurrentPath()
        })
    },

    render () 
    {
        return (
            <div id='lost-objects-management-view'>
                <card-list ref='cardList' card-object-name='objet' cards={this.objects} tags={this.tags}
                    on-cards-tags-changed={this.onCardsTagsChanged} on-tags-modified={this.onTagsModified}
                    tags-validator={this.tagsValidator} card-content-creator={this.createCardCollapsibleContent} />
            </div>
        )
    },

    data () 
    {
        return {
        }
    },

    mounted ()
    {
    },

    computed:
    {
        objects ()
        {
            return this.$store.state.lostObjects.objectsList.map(o =>
            {
                const isFound = o.tags.includes('found')
                var title = `Objet ${isFound ? 'trouvé' : 'perdu'} n°${o.id} : `
                if (o.linkedToObjectId)
                {
                    title += 'Suivi de l\'objet n°' + o.linkedToObjectId
                }
                else if (o.sender)
                {
                    title += o.sender.lastName
                }

                return { ...o, title }
            })
        },

        tags ()
        {
            return this.$store.state.lostObjects.tags
        }
    },

    methods:
    {
        createCardCollapsibleContent (card)
        {
            const geolocation = GeolocationUtils.isGeolocationString(card.location)
                ? GeolocationUtils.stringToCoords(card.location)
                : undefined

            const linkedObjectIds = this.getObjectsLinkedTo(card).map(o => o.id)
            const filteredIds = this.$refs.cardList ? this.$refs.cardList.filter.ids : []
            const areLinkedObjectsAlreadyShown = ArrayUtils.areEqualSets([...linkedObjectIds, card.id], filteredIds)
            
            card.sender = card.sender || {}

            return <div>
                <div class='section'>
                    <image-enlarger class='card-picture' v-show={!StringUtils.isEmptyString(card.pictureUrl)}
                        src={card.pictureUrl} />
                </div>
                <div class='section'>
                    <Button v-show={linkedObjectIds.length > 0 && !areLinkedObjectsAlreadyShown} text={`Afficher les objets liés (${linkedObjectIds.length})`}
                        on-click={() => this.showObjectLinkedObjects(card)} class='show-linked-objects-btn' />
                    <div>
                        <p v-show={!geolocation && card.location}><strong>Localisation: </strong><div class='markable'>{card.location}</div></p>
                        <p class='title'>Envoyé par</p>
                        <p><strong>Nom: </strong><div class='markable'>{card.sender.lastName}</div></p>
                        <p><strong>Prénom: </strong><div class='markable'>{card.sender.firstName}</div></p>
                        <p>
                            <strong>Mél: </strong>
                            <a href={`mailto:${card.sender.email}`} target='_blank' class='markable'>{card.sender.email}</a>
                        </p>
                        <p>
                            <strong>Tél: </strong>
                            <a href={`tel:${card.sender.phone}`} target='_blank' class='markable'>{card.sender.phone}</a>
                        </p>
                    </div>
                    <p class='date'>Rapporté le {DateUtils.dateToString(card.date, 'iso', 'fr')}</p>
                </div>
                <div class='section action-buttons'>
                    {
                        geolocation && (
                            <Button outlined={true} type='light'
                                text={`Géolocalisation (~${Math.round(geolocation.accuracy)}m)`} 
                                on-click={() => window.open(GeolocationUtils.generateGoogleMapLinkFromCoords(geolocation), '_system')} />
                        )
                    }
                    <Button outlined={true} type='light' text='Télécharger le PDF'
                        on-click={(event, button) => this.downloadPDFClicked(button, card)} />
                </div>
            </div>
        },

        async downloadPDFClicked (button, cardData)
        {
            button.showSpinner = true
            const pdf = await PDFUtils.generateObjectPDFAsync(cardData)
            pdf.getBlob(async blob =>
            {
                await FileUtils.writeFileToDownloadsAsync(pdf.docDefinition.info.title + '.pdf', blob)
                button.showSpinner = false
            })
        },

        /**
         * @param {Array<object>} cards
         */
        async onCardsTagsChanged (cards)
        {
            const changeData = cards.map(c => ({ id: c.id, tags: c.tags }))
            const updatedObjects = await LostObjectsServices.patchLostObjects(EnvironmentUtils.cityId, this.$store.state.user.token, changeData)
            await this.$store.commit('lostObjects/updateObjectsList', updatedObjects)
        },

        async onTagsModified (tags)
        {
            const updated = await LostObjectsServices.setLostObjectsTagsDefinition(EnvironmentUtils.cityId, this.$store.state.user.token, tags)
            await this.$store.commit('lostObjects/setTags', updated)
        },

        tagsValidator (set, tags, object)
        {
            if (object.linkedToObjectId && tags.published)
            {
                tags.published = false

                this.$toast.warning(`Ce signalement est lié à un objet déjà existant (n°${object.linkedToObjectId}),`
                    + ' il ne peut donc pas être publié car il ne représente pas un objet distinct par lui-même.', {duration: 10000})
            }

            if (tags.published && tags.resolved)
            {
                tags.published = false
            }

            const states = ['lost', 'found', 'resolved']
            const statesHaving = Object.entries(tags).filter(t => states.includes(t[0]) && t[1])

            if (states.includes(set) && statesHaving.length > 1)
            {
                states.filter(s => s != set).forEach(s => tags[s] = false)
            }

            if (statesHaving.length == 0)
            {
                tags[states.filter(s => s != set)[0]] = true
            }
        },

        getObjectsLinkedTo (object)
        {
            return this.objects
                .filter(o => (object.linkedToObjectId == o.id || o.linkedToObjectId == object.id || (o.linkedToObjectId && o.linkedToObjectId == object.linkedToObjectId)) && object.id != o.id)
        },

        showObjectLinkedObjects (object)
        {
            this.$refs.cardList.filter.search = '',
            this.$refs.cardList.filter.tag = '',
            this.$refs.cardList.filter.ids = [...this.getObjectsLinkedTo(object).map(o => o.id), object.id]
        }
    },

    components:
    {
        CardList,
        Button,
        ImageEnlarger
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#lost-objects-management-view
{
    .show-linked-objects-btn
    {
        width: -webkit-fill-available;
        margin-bottom: 5px;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/LostObjectsManagement.vue